import { useCallback } from 'react';
import useSWR from 'swr';

import {
  violationNotesCreate,
  violationNotesList,
} from '~/data/generated/endpoints/violation-notes/violation-notes';
import { parseApiResponse } from '~/data/parseApiResponse';
import { violationNotesResponseSchema } from '~/data/schemas/compliance';

const violationNotesFetcher = async (violationId: string) => {
  const { data } = await violationNotesList({
    violation_id: violationId,
    ordering: 'created_at',
  });
  return parseApiResponse(violationNotesResponseSchema, data, 'ViolationNotes');
};

/**
 * `enabled: false` holds the read without unmounting the hook — the elevator page renders
 * a notes section per violation and only the expanded ones fetch, so a car with a dozen
 * violations doesn't fire a dozen requests.
 */
export const useViolationNotes = (violationId: string, { enabled = true } = {}) => {
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? ['violationNotes', violationId] : null,
    () => violationNotesFetcher(violationId),
    {
      revalidateOnFocus: true,
    },
  );

  const addNote = useCallback(
    async (note: string) => {
      try {
        await violationNotesCreate({
          violation: violationId,
          note,
        });
        await mutate();
      } catch (error) {
        console.error('Error adding violation note:', error);
        throw error;
      }
    },
    [violationId, mutate],
  );

  return {
    notes: data?.results ?? [],
    metadata: data?.metadata,
    isLoading,
    error,
    addNote,
  };
};
